import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { usePrayer } from "../../context/PrayerContext";
import { captureCardImage } from "../../utils/captureCardImage";
import {
  trackPrayerResult,
  trackPrayerShare,
  trackPrayerRetry,
  trackEvent,
} from "../../utils/analytics";
import styles from "./ResultPage.module.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import area1 from "../../assets/images/area_1.webp";
import area2 from "../../assets/images/area_2.webp";
import area3 from "../../assets/images/area_3.webp";
import area4 from "../../assets/images/area_4.webp";
import area5 from "../../assets/images/area_5.webp";
import area6 from "../../assets/images/area_6.webp";
import area7 from "../../assets/images/area_7.webp";
import area8 from "../../assets/images/area_8.webp";
import area9 from "../../assets/images/area_9.webp";
import cardBottom from "../../assets/images/result_card_bottom.webp";
import prayTitle from "../../assets/images/pray_title.webp";
import iconBg from "../../assets/icons/icon_bg.webp";
import resultDesc from "../../assets/images/result_desc.webp";
import iconRetry from "../../assets/icons/icon_retry.webp";
import iconDownload from "../../assets/icons/icon_download.webp";
import g2aLogo from "../../assets/icons/icon_g2a.webp";
import iconKakao from "../../assets/icons/icon_kakao.webp";
import prayersData from "../../data/prayers_collection.json";
import sendToAll from "../../assets/images/send_to_all.webp";
import modalImage from "../../assets/images/modal.webp";
import news1 from "../../assets/images/news_1.webp";
import news2 from "../../assets/images/news_2.webp";
import news3 from "../../assets/images/news_3.webp";
import news4 from "../../assets/images/news_4.webp";
import news5 from "../../assets/images/news_5.webp";
import news6 from "../../assets/images/news_6.webp";
import news7 from "../../assets/images/news_7.webp";
import news8 from "../../assets/images/news_8.webp";
import news9 from "../../assets/images/news_9.webp";

// 한국어 줄바꿈 보정
//  1) 닫는 따옴표·괄호 뒤에 한글 조사가 바로 붙는 경우(예: ‘세상의 빛’으로) 그 사이에서 끊기지 않게 워드 조이너(U+2060)
//  2) 짧은 작은따옴표 구절(‘세상의 빛’ 등 12자 이하)은 안쪽 공백을 NBSP로 바꿔 한 줄에 붙어 있게
//  3) 마지막 어절이 혼자 남지 않게(고아 방지) 마지막 공백을 NBSP로 — 브라우저와 무관하게 동일하게 동작
const noBreakAfterQuote = (t) =>
  t
    .replace(/‘([^’]{1,12})’/g, (m) => m.replace(/ /g, "\u00a0"))
    .replace(/([’”)\]])(?=[가-힣])/g, "$1\u2060")
    .replace(/ (?=[^ ]+$)/, "\u00a0");

const ResultPage = () => {
  const navigate = useNavigate();
  const { selectedPrayer, setSelectedPrayer } = usePrayer();
  const [showModal, setShowModal] = useState(false);
  const [modalClosed, setModalClosed] = useState(false); // 모달이 한 번 닫혔는지 추적
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentPrayer, setCurrentPrayer] = useState(null);
  const cardRef = useRef(null);
  // 이미지 저장 미리보기: null | { status: "loading" } | { status: "ready", url, file, mode }
  const [cardImage, setCardImage] = useState(null);

  // 화면이 짧은 브라우저(카톡 인앱·툴바 있는 사파리)에서 카드가 위아래로 잘리지 않게, 카드 블록을 비율 유지한 채 축소
  // 카드 블록 높이 645(프레임 10 + 카드 600 + 하단 35) + 위 여백 50 + 숨통 16 → 뷰포트에 안 들어가면 zoom 축소(하한 0.82)
  useEffect(() => {
    const fit = () => {
      const vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      const z = Math.max(0.82, Math.min(1, (vh - 66) / 645));
      document.documentElement.style.setProperty("--card-zoom", z.toFixed(3));
    };
    fit();
    window.addEventListener("resize", fit);
    if (window.visualViewport) window.visualViewport.addEventListener("resize", fit);
    return () => {
      window.removeEventListener("resize", fit);
      if (window.visualViewport) window.visualViewport.removeEventListener("resize", fit);
    };
  }, []);
  // 뉴스 카드 데이터
  // 2026 Q&A 카드뉴스 9장 (2025 집회 소식 10장에서 교체). alt는 각 카드의 실제 문구
  const newsCards = [
    { id: 1, image: news1, title: "G2A 자주 묻는 질문들 Q&A" },
    { id: 2, image: news2, title: "G2A는 정확히 어떤 집회인가요?" },
    { id: 3, image: news3, title: "각 순서에 어떤 라인업이 나오는지 미리 알 수 있나요?" },
    { id: 4, image: news4, title: "새가족을 데려가도 될까요?" },
    { id: 5, image: news5, title: "10시간 예배인데 식사는 언제 어떻게 하나요? 부스/쉼터를 운영하나요?" },
    { id: 6, image: news6, title: "지역에서 참여할 경우, 버스 대절이 가능한가요?" },
    { id: 7, image: news7, title: "교회가 단체로 신청해도 되나요?" },
    { id: 8, image: news8, title: "혼자인데, 힘들거나 외롭지 않을까요?" },
    { id: 9, image: news9, title: "청소년/청년들만을 위한 집회인가요?" },
  ];

  // 카테고리에 따른 area 이미지 매핑
  const getAreaImage = (category) => {
    switch (category) {
      case "시대를 향한 기도":
        return area8;
      case "복음전파":
        return area1;
      case "열방 / 타문화선교":
        return area2;
      case "부흥을 위한 기도":
        return area7;
      case "청소년을 향한 기도":
        return area9;
      case "캠퍼스를 향한 기도":
        return area4;
      case "일터를 향한 기도":
        return area5;
      default:
        return area6;
    }
  };

  // 랜덤 기도문 선택 (Fisher-Yates 셔플 알고리즘 사용)
  const selectRandomPrayer = () => {
    const prayers = [...prayersData.prayers]; // 원본 배열 복사

    // Fisher-Yates 셔플로 배열을 완전히 섞음
    for (let i = prayers.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [prayers[i], prayers[j]] = [prayers[j], prayers[i]];
    }

    // 섞인 배열에서 첫 번째 요소 반환
    return prayers[0];
  };

  // 기도문 준비 및 애니메이션 시작
  useEffect(() => {
    let prayerToUse;

    // 기도문 결정
    if (selectedPrayer) {
      prayerToUse = selectedPrayer;
    } else {
      prayerToUse = selectRandomPrayer();
      setSelectedPrayer(prayerToUse);
    }

    // 현재 기도문 설정
    setCurrentPrayer(prayerToUse);

    // GA4 이벤트 추적: 기도문 결과 조회
    trackPrayerResult(prayerToUse.category);

    // 기도문이 설정된 후 애니메이션 시작
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // 09-12 요청: 스크롤 트리거 대신, 카드가 보이고 6초 뒤에 하단 모달을 올린다.
  // 한 번 닫으면 이 화면에 머무는 동안 다시 열지 않음(다시 뽑기로 재진입하면 다시 뜸).
  const MODAL_DELAY_MS = 15000; // 09-14 유현님: 결과 카드 뜨고 15초 뒤 (6초 → 15초)
  useEffect(() => {
    if (!isLoaded || modalClosed) return;
    const timer = setTimeout(() => {
      setShowModal(true);
    }, MODAL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isLoaded, modalClosed]);

  const onKakao = () => {
    // GA4 이벤트 추적: 카카오 공유
    trackPrayerShare("kakao", currentPrayer?.category);

    if (!window.Kakao) {
      alert("카카오톡 공유를 위해 페이지를 새로고침해주세요.");
      return;
    }

    if (!window.Kakao.isInitialized()) {
      window.Kakao.init(process.env.REACT_APP_KAKAO_KEY);
    }

    window.Kakao.Link.sendDefault({
      objectType: "feed",
      content: {
        title: `🔥 오늘의 기도영역 '${currentPrayer.category}'`,
        description: (() => {
          // 화면용 고정 줄바꿈은 카톡 메시지에선 풀어서 전달
          const flatTitle = currentPrayer.title.replace(/\n/g, " ");
          return flatTitle.length > 100
            ? flatTitle.substring(0, 100) + "..."
            : flatTitle + "❗️";
        })(),
        imageUrl: window.location.origin + "/kakao_thumb.png",
        link: {
          mobileWebUrl: "https://g2a-pray.com",
          webUrl: "https://g2a-pray.com",
        },
      },
      buttons: [
        {
          title: "나도 기도제목 받기!",
          link: {
            mobileWebUrl: "https://g2a-pray.com",
            webUrl: "https://g2a-pray.com",
          },
        },
      ],
    });
  };

  // 화면의 카드를 그대로 캡처해 미리보기로 띄운다. 캡처가 끝난 뒤 새 창을 열면 모바일·인앱 브라우저가
  // 팝업으로 막으므로, 저장(공유·다운로드)은 미리보기에서 한 번 더 눌러 실행한다.
  const handleDownloadImage = async () => {
    // GA4 이벤트 추적: 이미지 다운로드
    trackPrayerShare("download", currentPrayer?.category);
    if (cardImage || !cardRef.current) return;

    setCardImage({ status: "loading" });
    try {
      const blob = await captureCardImage(cardRef.current, {
        lineSplitSelector: `.${styles.titleMark}`,
      });
      const file = new File([blob], "g2a-prayer.png", { type: "image/png" });
      const isTouch = window.matchMedia("(pointer: coarse)").matches;
      const canShareFile = !!(navigator.canShare && navigator.canShare({ files: [file] }));
      // share: 모바일 공유 시트(사진 저장·인스타 스토리) / download: PC / longpress: 파일 공유가 안 되는 인앱 브라우저
      const mode = !isTouch ? "download" : canShareFile ? "share" : "longpress";
      setCardImage({ status: "ready", url: URL.createObjectURL(blob), file, mode });
    } catch (error) {
      console.error("이미지 생성 실패:", error);
      setCardImage(null);
      alert("이미지 생성에 실패했습니다. 잠시 후 다시 시도해주세요.");
    }
  };

  const saveCardImage = async () => {
    const { url, file, mode } = cardImage;
    if (mode === "share") {
      try {
        await navigator.share({ files: [file] });
      } catch (error) {
        // 공유 시트를 그냥 닫은 경우(AbortError)는 무시
        if (error.name !== "AbortError") {
          alert("저장이 안 되면 이미지를 길게 눌러 저장해 주세요.");
        }
      }
      return;
    }
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const closeCardImage = () => {
    if (!cardImage || cardImage.status === "loading") return;
    URL.revokeObjectURL(cardImage.url);
    setCardImage(null);
  };

  // 로딩 중이거나 기도문이 준비되지 않았을 때
  if (!currentPrayer) {
    return <div className={styles.resultPage}>로딩 중...</div>;
  }

  return (
    <div className={styles.resultPage}>
      {/* 상단 배너 섹션 */}
      <div
        ref={cardRef}
        className={`${styles.bannerSection} ${
          isLoaded ? styles.fadeInUp : styles.fadeInUpInitial
        }`}
      >
        <div className={styles.bannerCard}>
          <img
            src={getAreaImage(currentPrayer.category)}
            alt={currentPrayer.category}
            style={{
              // height: 90,
              maxWidth: 300,
              width: "80%",
              position: "absolute",
              top: 40, // 09-07 유현님: 카테고리 영역 조금 더 아래로 (30→40)
              left: "50%",
              transform: "translateX(-50%)",
            }}
          />
          <div className={styles.bannerStats}>
            <div>
              {/* 제목: 데이터에 손 줄바꿈(\n)이 있으면 그대로(의미 단위), 없으면 260px 컬럼에서 균형 자동 줄바꿈 */}
              {/* 09-14 유현님: 제목 끝에 "!" (데이터의 마침표는 "!"로 대체, JSON은 손대지 않음) */}
              {currentPrayer.title.split("\n").map((line, i, arr) => (
                <React.Fragment key={i}>
                  <span className={styles.titleMark}>{noBreakAfterQuote(i === arr.length - 1 ? line.trim().replace(/[.!]+$/, "") + "!" : line.trim())}</span>
                  {i < arr.length - 1 && <br />}
                </React.Fragment>
              ))}
            </div>
          </div>
          {currentPrayer.author && (
            <div className={styles.bannerFooter}>{currentPrayer.author}</div>
          )}

          {/* PRAY LIST 제목 (카드 내부) */}
          <div
            className={`${styles.prayListTitle} ${
              isLoaded ? styles.fadeInUpDelay1 : styles.fadeInUpInitial
            }`}
          >
            <img
              src={prayTitle}
              style={{
                width: 200,
              }}
            />
          </div>

          {/* 기도 설명 텍스트 (카드 내부) */}
          <div
            className={`${styles.prayDescription} ${
              isLoaded ? styles.fadeInUpDelay2 : styles.fadeInUpInitial
            }`}
          >
            {(() => {
              // “(출처) 성구” 를 분해해 기도문 → 한 줄 공백 → 성구 → 출처 순으로 표시. (09-13 요청: 기도문 먼저, 말씀 아래)
              // 성구 앞에 문장이 있는 편(시대 9)은 그 문장을 기도문 앞에 붙인다.
              // 데이터의 고정 줄바꿈(\n)은 320px 컬럼용이라 풀고, 290px 컬럼에서 어절 단위(keep-all)로 자동 줄바꿈.
              const c = currentPrayer.content;
              const m = c.match(/“\(([^)]+)\)\s*([^”]*)”/);
              if (!m) return c;
              const flat = (t) => noBreakAfterQuote(t.replace(/\n/g, " ").trim());
              const pre = flat(c.slice(0, m.index));
              const post = flat(c.slice(m.index + m[0].length));
              return (
                <>
                  {pre && <span className={styles.preBlock}>{pre}</span>}
                  {post && <span className={styles.bodyBlock}>{post}</span>}
                  {/* 09-14 유현님 선택 D4: 본문과 말씀 사이 양끝이 사라지는 1px 헤어라인 (시안 카드-구분장치-소소한-시안24-v2) */}
                  <i className={styles.fadeRule} aria-hidden="true" />
                  <span className={styles.verseBlock}>“{flat(m[2])}”</span>
                  <span className={styles.verseRef}>({m[1]})</span>
                </>
              );
            })()}
          </div>

          <div className={styles.brandFooter}>
            <div className={styles.scrollingText}>
              {[0, 1, 2, 3].map((i) => (
                <span className={styles.bandText} key={i}>
                  <em>ALL</em> PEOPLE <em>ALL</em> SPHERES <em>ALL</em> NATIONS
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 십자가 패턴 */}
      <div
        className={`${styles.crossPattern} ${
          isLoaded ? styles.fadeInUpDelay3 : styles.fadeInUpInitial
        }`}
      >
        <img
          src={iconBg}
          style={{
            width: "100%",
          }}
        />
      </div>

      {/* G2A 로고 섹션 */}
      <div
        className={`${styles.logoSection} ${
          isLoaded ? styles.fadeInUpDelay4 : styles.fadeInUpInitial
        }`}
      >
        <img
          src={resultDesc}
          style={{
            width: 250,
          }}
        />
      </div>

      {/* 광고배너영역 - 뉴스 카드 Swiper */}
      <div
        className={`${styles.newsSection} ${
          isLoaded ? styles.fadeInUpDelay4 : styles.fadeInUpInitial
        }`}
      >
        <Swiper
          modules={[Pagination, Navigation]}
          spaceBetween={12}
          slidesPerView="auto"
          centeredSlides={true}
          pagination={{
            clickable: true,
          }}
          navigation={false}
          className={styles.newsSwiper}
        >
          {newsCards.map((card) => (
            <SwiperSlide key={card.id} className={styles.newsSlide}>
              <img
                src={card.image}
                alt={card.title}
                className={styles.newsImage}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* 하단 버튼들 */}
      <div
        className={`${styles.bottomButtons} ${
          isLoaded ? styles.fadeInUpDelay5 : styles.fadeInUpInitial
        }`}
      >
        <div className={styles.topButtonRow}>
          <button
            className={styles.actionButton}
            onClick={() => {
              // GA4 이벤트 추적: 다시하기
              trackPrayerRetry();
              // 로딩페이지로 이동해서 새로운 기도문 선택
              navigate("/loading");
            }}
          >
            <span className={styles.buttonIcon}>
              <img
                src={iconRetry}
                style={{
                  width: 20,
                }}
              />
            </span>
            기도제목 다시 뽑기
          </button>
          <button className={styles.actionButton} onClick={handleDownloadImage}>
            <span className={styles.buttonIcon}>
              <img
                src={iconDownload}
                style={{
                  width: 20,
                }}
              />
            </span>
            이미지 저장하기
          </button>
        </div>
        <button
          className={styles.shareButton}
          onClick={() => {
            // GA4 이벤트 추적: 인터파크 예매
            trackEvent("click_interpark", "external_link", "interpark_ticket");
            window.open(
              "https://tickets.interpark.com/goods/26007354",
              "_blank"
            );
          }}
        >
          <span className={styles.g2aLogo}>
            <img
              src={g2aLogo}
              style={{
                width: 45,
                marginRight: 7,
              }}
            />
          </span>
          인터파크 예매하기
        </button>
        <button className={styles.kakaoButton} onClick={onKakao}>
          <span className={styles.buttonIcon}>
            <img
              src={iconKakao}
              style={{
                width: 18,
              }}
            />
          </span>
          내 기도제목 카카오톡 공유하기
        </button>
        <a href="https://www.g2a.co.kr" target="_blank">
          <div className={styles.linkText}>G2A 공식 홈페이지 바로가기</div>
        </a>
      </div>

      <div className={styles.tailBrand}>
        <img
          src={sendToAll}
          style={{
            width: 250,
            margin: "60px auto 80px",
          }}
        />
      </div>

      {/* 이미지 저장 미리보기 */}
      {cardImage && (
        <div className={styles.saveOverlay} onClick={closeCardImage}>
          <div
            className={styles.saveSheet}
            onClick={(e) => e.stopPropagation()}
          >
            {cardImage.status === "loading" ? (
              <div className={styles.saveLoading}>이미지를 만드는 중이에요…</div>
            ) : (
              <>
                <img
                  src={cardImage.url}
                  alt="내 기도제목 카드"
                  className={styles.savePreview}
                />
                {cardImage.mode !== "download" && (
                  <div className={styles.saveHint}>
                    {cardImage.mode === "share"
                      ? "이미지를 길게 눌러도 저장할 수 있어요"
                      : "이미지를 길게 눌러 저장해 주세요"}
                  </div>
                )}
                {cardImage.mode !== "longpress" && (
                  <button className={styles.saveButton} onClick={saveCardImage}>
                    이미지 저장하기
                  </button>
                )}
                <button
                  className={styles.saveCloseButton}
                  onClick={closeCardImage}
                >
                  닫기
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* 하단 모달 */}
      {showModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => {
            setShowModal(false);
            setModalClosed(true);
          }}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 모달 이미지 */}
            <div className={styles.modalImageContainer}>
              <img
                src={modalImage}
                alt="모달 이미지"
                className={styles.modalImage}
              />
            </div>

            <div className={styles.modalBody}>
              {/* 안내 텍스트 */}
              <div className={styles.modalText}>
                <div>내가 뽑은 기도제목을 스토리를 통해 공유해 주세요!</div>
                <div>@g2a_movement를 태그해주시면</div>
                <div>무한 리그램 해드립니다!</div>
              </div>

              {/* 확인 버튼 */}
              <button
                className={styles.modalConfirmButton}
                onClick={() => {
                  setShowModal(false);
                  setModalClosed(true);
                }}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultPage;
